import http from '../../shared/http.js';

// The navbar's search box: type a fragment of a name or a number anywhere in the
// app, get a short grouped list of the records you are allowed to see.
//
// The rows are real <a href> links under group headers, so a hit is ordinary
// navigation - middle-click, copy-link-address and Enter all keep working
// without a line of code here. That is also why there is no role="combobox",
// no role="listbox" and no virtual focus: views/aufgaben/_form.pug already
// records the decision against hand-built comboboxes, and real focus on real
// links makes this a menu rather than one.

// Below this the server answers with empty groups, so asking is pointless. Kept
// in step with MIN_ZEICHEN in controllers/sucheController.js - the server is
// what enforces it, this only decides whether to spend the request.
const MIN_ZEICHEN = 2;

// Long enough that typing a customer name is one request rather than fifteen,
// short enough that the panel still feels immediate.
const DEBOUNCE_MS = 350;

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const hinweis = (text) =>
  `<li class="p-4 text-center text-sm text-base-content/60">${escapeHtml(text)}</li>`;

// One group: a header and its rows. An empty group renders nothing at all rather
// than a header over a blank space - a heading that names no results is worse
// than no heading.
const gruppe = (titel, eintraege) => {
  if (eintraege.length === 0) return '';

  const zeilen = eintraege
    .map(
      ({ href, titel: zeile, zusatz }) => `
        <li>
          <a href="${escapeHtml(href)}" class="flex-col items-start gap-0">
            <span class="truncate">${escapeHtml(zeile)}</span>
            <span class="text-xs text-base-content/60">${escapeHtml(zusatz)}</span>
          </a>
        </li>`
    )
    .join('');

  return `<li class="menu-title">${escapeHtml(titel)}</li>${zeilen}`;
};

export function initGlobalSuche() {
  const menu = document.getElementById('global-suche');
  if (!menu) return;

  const feld = document.getElementById('global-suche-feld');
  const treffer = document.getElementById('global-suche-treffer');

  let timer = null;
  let inFlight = null;

  const close = () => menu.removeAttribute('open');

  const render = (html) => {
    treffer.innerHTML = html;
  };

  const suchen = async (q) => {
    if (q.length < MIN_ZEICHEN) {
      render(hinweis('Mindestens 2 Zeichen'));
      return;
    }

    // A slow response for a term the user has already typed past must not
    // overwrite a newer one, so each request supersedes the last - the same
    // arrangement shared/liveList.js uses.
    inFlight?.abort();
    const controller = new window.AbortController();
    inFlight = controller;

    treffer.setAttribute('aria-busy', 'true');

    try {
      const { data } = await http.get('/suche', {
        params: { q },
        signal: controller.signal
      });

      const kunden = data.data.kunden.map((kunde) => ({
        href: `/kunden/${kunde._id}`,
        titel: kunde.name,
        zusatz: [kunde.kundeNr, kunde.ort].filter(Boolean).join(' · ')
      }));

      const jobs = data.data.jobs.map((job) => ({
        href: `/jobs/${job._id}`,
        titel: job.titel,
        zusatz: [job.jobNr, job.kunde?.name].filter(Boolean).join(' · ')
      }));

      const html = gruppe('Kunden', kunden) + gruppe('Jobs', jobs);
      render(html || hinweis('Keine Treffer'));
    } catch (err) {
      // A superseded request is the normal case, not a failure.
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      // The 401 interceptor in shared/http.js has already redirected a dead
      // session, so anything reaching here is a real fault. It stays inside the
      // panel rather than becoming a toast over whatever page somebody is on.
      render(hinweis('Suche nicht möglich'));
    } finally {
      if (inFlight === controller) {
        inFlight = null;
        treffer.removeAttribute('aria-busy');
      }
    }
  };

  feld.addEventListener('input', () => {
    const q = feld.value.trim();

    window.clearTimeout(timer);
    // The in-flight request is dropped on every keystroke rather than only when
    // the next one starts: the answer to a term that is no longer in the box is
    // never wanted, even while the debounce is still waiting.
    inFlight?.abort();

    if (q.length < MIN_ZEICHEN) {
      render(hinweis('Mindestens 2 Zeichen'));
      return;
    }

    render(hinweis('Wird geladen …'));
    timer = window.setTimeout(() => suchen(q), DEBOUNCE_MS);
  });

  // The caret lands in the box on opening, so the icon is one click rather than
  // two. `toggle` rather than `click`, so keyboard Enter and Space get it too -
  // which is the entire reason this is a <details>.
  menu.addEventListener('toggle', () => {
    if (menu.open) feld.focus();
  });

  // ArrowDown and ArrowUp rove across the result links, with ArrowDown from the
  // input entering the list. Enter needs no handler at all: the rows are
  // anchors, so the browser follows them.
  const links = () => Array.from(treffer.querySelectorAll('a'));

  // `von` is the link focus sits on, or null when it is still in the input.
  const roving = (event, von) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const alle = links();
    if (alle.length === 0) return;

    event.preventDefault();

    if (event.key === 'ArrowDown') {
      // From the input into the first row; from the last row back to the first.
      const index = von ? alle.indexOf(von) : -1;
      (alle[index + 1] || alle[0]).focus();
      return;
    }

    // ArrowUp from the first row returns to the input rather than wrapping to
    // the last - the box is where somebody goes to change the term, so it is
    // the natural thing above the first result.
    const index = alle.indexOf(von);
    (index <= 0 ? feld : alle[index - 1]).focus();
  };

  feld.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown') roving(event, null);
  });

  treffer.addEventListener('keydown', (event) => {
    const link = event.target.closest('a');
    if (link) roving(event, link);
  });

  // <details> is the accessible choice here but natively closes only via its own
  // summary, so the outside-click and Escape behaviour is added the way
  // userMenu.js and aufgabenGlocke.js both add it. Copied rather than shared,
  // for the reason the bell gives: three menus needing the same three listeners
  // is not shared behaviour.
  document.addEventListener('click', (event) => {
    if (!menu.contains(event.target)) close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !menu.hasAttribute('open')) return;
    close();
    menu.querySelector('summary')?.focus();
  });

  // A row navigates; the panel should not stay open behind it.
  treffer.addEventListener('click', (event) => {
    if (event.target.closest('a')) close();
  });
}
